import { ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { publicRoutes } from '@/constant/auth-public-routes';
import {
  clearOrgSelectionRequired,
  isOrgSelectionRequired,
  POST_AUTH_PATH,
} from '@/lib/auth/org-selection';
import { mustSelectNonDefaultOrganization } from '@/lib/auth/org-access';
import { useGetAccount } from '@/modules/profile/hooks/use-account';
import { ForceOrganizationDialog } from '@/components/core/force-organization-dialog/force-organization-dialog';

/**
 * useAuthState Hook
 *
 * A custom hook that provides authentication state with mounting status tracking.
 * It synchronizes with the global auth store and ensures components can detect
 * when auth state is fully loaded.
 *
 * @returns {AuthState} An object containing authentication status information
 *   - isMounted: Whether the auth state has been initialized
 *   - isAuthenticated: Whether the user is authenticated
 *
 * @example
 * const { isMounted, isAuthenticated } = useAuthState();
 *
 * if (!isMounted) {
 *   return <LoadingSpinner />;
 * }
 *
 * return isAuthenticated ? <AuthenticatedApp /> : <UnauthenticatedApp />;
 */

interface AuthState {
  isMounted: boolean;
  isAuthenticated: boolean;
}

export const useAuthState = () => {
  const { isAuthenticated } = useAuthStore();
  const [isAuth, setIsAuth] = useState<AuthState>({
    isMounted: false,
    isAuthenticated: false,
  });

  useEffect(() => {
    setIsAuth({
      isMounted: true,
      isAuthenticated: isAuthenticated,
    });
  }, [isAuthenticated]);

  return isAuth;
};

/**
 * ClientMiddleware Component
 *
 * A protection layer that controls rendering based on authentication state,
 * automatically redirecting unauthenticated users away from protected routes.
 *
 * Features:
 * - Blocks access to protected routes for unauthenticated users
 * - Redirects to login page when authentication is required
 * - Allows access to public routes without authentication
 * - Full-page org selection only when self-signup flagged AND user is Default-only
 * - Skips org selection (clears flag → reports overview) when org is already non-Default
 * - Shows a blocking org popup for existing Default-only users (no redirect)
 * - Prevents content flash by not rendering until auth state is confirmed
 *
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components to render when access is granted
 * @returns {JSX.Element|null} The children when access is granted, null otherwise
 *
 * @example
 * <ClientMiddleware>
 *   <ProtectedRoutes />
 * </ClientMiddleware>
 */

interface ClientMiddlewareProps {
  children: ReactNode;
}

export const ClientMiddleware: React.FC<ClientMiddlewareProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const { isMounted, isAuthenticated } = useAuthState();
  const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
  const isPublicRoute = publicRoutes.includes(currentPath);
  const isSelectOrganizationRoute = currentPath === '/select-organization';

  const { data: account, isFetched: isAccountFetched } = useGetAccount({
    enabled: isMounted && isAuthenticated,
  });

  // Check if we're processing an SSO callback (has code and state parameters)
  const urlParams = new URLSearchParams(location.search);
  const isSSOCallback = !!(urlParams.get('code') && urlParams.get('state'));

  const isDefaultOnlyUser =
    isAccountFetched && mustSelectNonDefaultOrganization(account, selectedOrgId);

  const signupOrgFlagSet = isAuthenticated && isOrgSelectionRequired();

  /** Full-page select only when flagged and still Default-only (or account not loaded yet). */
  const requiresSignupOrgPage =
    signupOrgFlagSet && (!isAccountFetched || isDefaultOnlyUser);

  const showForceOrgDialog =
    isAuthenticated &&
    isAccountFetched &&
    !requiresSignupOrgPage &&
    !isSelectOrganizationRoute &&
    !isPublicRoute &&
    isDefaultOnlyUser;

  useLayoutEffect(() => {
    if (isSSOCallback) {
      return;
    }

    if (!isMounted) return;

    if (!isAuthenticated && !isPublicRoute) {
      navigate('/login');
      return;
    }

    if (!isAuthenticated) return;

    // Already on a non-Default org — clear signup gate and leave select-organization
    if (signupOrgFlagSet && isAccountFetched && !isDefaultOnlyUser) {
      clearOrgSelectionRequired();
      if (isSelectOrganizationRoute) {
        navigate(POST_AUTH_PATH, { replace: true });
      }
      return;
    }

    if (requiresSignupOrgPage && !isSelectOrganizationRoute) {
      navigate('/select-organization', { replace: true });
    }
  }, [
    isAuthenticated,
    isMounted,
    isPublicRoute,
    isSSOCallback,
    isSelectOrganizationRoute,
    requiresSignupOrgPage,
    signupOrgFlagSet,
    isAccountFetched,
    isDefaultOnlyUser,
    navigate,
    currentPath,
  ]);

  // Don't block rendering if we're processing SSO callback
  if (isSSOCallback) {
    return <>{children}</>;
  }

  if ((!isMounted || !isAuthenticated) && !isPublicRoute) return null;

  return (
    <>
      {children}
      <ForceOrganizationDialog open={showForceOrgDialog} />
    </>
  );
};
